import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  buildRecipients,
  renderEmail,
  DEFAULT_SUBJECT,
  DEFAULT_INTRO,
  DEFAULT_SIGNOFF,
} from "../utils/scheduleEmails";
import { connectGmail, disconnectGmail, sendMessage, isConfigured } from "../utils/gmail";
import { membersCsv, recipientsCsv, downloadCsv, exportFilename } from "../utils/exportSchedules";

// Term-kickoff mail-out: one message per email address, sent through the
// admin's own Gmail account. The flow is deliberately three-step —
// preview every message, send one test to yourself, then send the batch —
// because there is no unsend once 200-odd messages are out.
//
// The send log is kept in localStorage per term so a re-opened tab still
// shows who has already been emailed and what failed.

const LOG_KEY = "kac-email-log";
const SEND_DELAY_MS = 300; // keeps the batch well under Gmail's per-user rate

const loadLog = () => {
  try {
    return JSON.parse(localStorage.getItem(LOG_KEY)) || {};
  } catch {
    return {};
  }
};

// A plain textarea with a link button. The body has to stay free text — it is
// escaped on the way into the message, so no pasted HTML can reach a member's
// inbox — which means a link is written as [label](url). The button only saves
// the admin from remembering that syntax; typing it by hand works just as well.
function RichField({ label, value, onChange, rows, id }) {
  const ref = useRef(null);

  function insertLink() {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;

    const url = window.prompt("Link to (web or email address):", "https://");
    if (!url || !url.trim()) return;
    const text = value.slice(start, end) || window.prompt("Text to show:", "") || url.trim();
    const markup = `[${text}](${url.trim()})`;

    onChange(value.slice(0, start) + markup + value.slice(end));
    // Leave the caret after what was just inserted, so the admin can keep
    // typing where they left off instead of hunting for their place.
    const caret = start + markup.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </label>
        <button
          type="button"
          onClick={insertLink}
          className="text-xs text-blue-600 hover:text-blue-800"
          title="Wrap the selected text in a link"
        >
          🔗 Link
        </button>
      </div>
      <textarea
        id={id}
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
    </div>
  );
}

export default function EmailView({ players, groups, activeTermName, onNotify }) {
  const [term, setTerm] = useState(activeTermName || "");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [signoff, setSignoff] = useState(DEFAULT_SIGNOFF);
  const [includeGroupmates, setIncludeGroupmates] = useState(false);

  const [account, setAccount] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [authError, setAuthError] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [previewEmail, setPreviewEmail] = useState(null);
  const [log, setLog] = useState(() => loadLog());
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [confirming, setConfirming] = useState(false);

  const recipients = useMemo(
    () => buildRecipients(players, groups),
    [players, groups]
  );

  const options = useMemo(
    () => ({ term, subject, intro, signoff, includeGroupmates }),
    [term, subject, intro, signoff, includeGroupmates]
  );

  // Follow the active term, including when it's saved or switched from the
  // Terms dialog while this tab is open. A manual edit sticks until then.
  useEffect(() => {
    if (activeTermName) setTerm(activeTermName);
  }, [activeTermName]);

  // Default selection: everyone who actually has a session to tell them about.
  useEffect(() => {
    setSelected(new Set(recipients.filter(r => r.sessionCount > 0).map(r => r.email)));
  }, [recipients]);

  useEffect(() => {
    if (!previewEmail && recipients.length > 0) setPreviewEmail(recipients[0].email);
  }, [recipients, previewEmail]);

  const termLog = log[term] || {};
  const statusFor = email => termLog[email]?.status;

  const writeLog = useCallback((email, entry) => {
    setLog(prev => {
      const next = { ...prev, [term]: { ...(prev[term] || {}), [email]: entry } };
      try {
        localStorage.setItem(LOG_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [term]);

  const withSessions = recipients.filter(r => r.sessionCount > 0).length;
  const withoutSessions = recipients.length - withSessions;
  // Not "has no email of their own" — a junior with no address is reached
  // through their household's. Unreachable means no address covers them.
  const covered = new Set(recipients.flatMap(r => r.people.map(p => p.id)));
  const unreachable = (players || []).filter(p => !covered.has(p.id)).length;
  const sentCount = Object.values(termLog).filter(e => e.status === "sent").length;
  const failed = recipients.filter(r => statusFor(r.email) === "failed");

  const previewRecipient = recipients.find(r => r.email === previewEmail) || null;
  const preview = previewRecipient ? renderEmail(previewRecipient, options) : null;

  function toggle(email) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function handleConnect() {
    setConnecting(true);
    setAuthError(null);
    try {
      setAccount(await connectGmail());
    } catch (e) {
      setAuthError(e.message || "Could not connect to Gmail.");
    } finally {
      setConnecting(false);
    }
  }

  function handleDisconnect() {
    disconnectGmail();
    setAccount(null);
  }

  async function handleTestSend() {
    if (!previewRecipient || !account) return;
    setSending(true);
    try {
      const message = renderEmail(previewRecipient, options);
      await sendMessage({ ...message, to: account, subject: `[TEST] ${message.subject}` });
      onNotify?.({ message: `Test copy of ${previewRecipient.email}'s email sent to ${account}.` });
    } catch (e) {
      onNotify?.({ message: `Test send failed: ${e.message}` });
    } finally {
      setSending(false);
    }
  }

  // Sequential on purpose: a rate-limit response mid-flight should stop one
  // message, not race a dozen others into the same failure.
  async function runSend(targets) {
    setConfirming(false);
    setSending(true);
    setProgress({ done: 0, total: targets.length });
    let ok = 0;
    let bad = 0;

    for (let i = 0; i < targets.length; i++) {
      const recipient = targets[i];
      try {
        await sendMessage(renderEmail(recipient, options));
        writeLog(recipient.email, { status: "sent", at: new Date().toISOString() });
        ok++;
      } catch (e) {
        writeLog(recipient.email, {
          status: "failed",
          at: new Date().toISOString(),
          error: e.message || "Send failed",
        });
        bad++;
      }
      setProgress({ done: i + 1, total: targets.length });
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, SEND_DELAY_MS));
    }

    setSending(false);
    setProgress(null);
    onNotify?.({
      message:
        bad === 0
          ? `Sent ${ok} email${ok === 1 ? "" : "s"}.`
          : `Sent ${ok}, ${bad} failed — failures are listed below and can be retried.`,
    });
  }

  const toSend = recipients.filter(r => selected.has(r.email));
  const ready = account && !sending && toSend.length > 0 && term.trim() !== "";

  // Downloads work with or without Gmail connected — the export is also how
  // the schedule gets checked before anything is sent.
  const exportButtons = (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => downloadCsv(exportFilename("member-schedules", term), membersCsv(players, groups))}
        className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg px-4 py-2 transition-colors"
        title="One row per member per session — the full schedule as members will see it"
      >
        ⬇ Member schedules (CSV)
      </button>
      <button
        onClick={() => downloadCsv(exportFilename("email-recipients", term), recipientsCsv(players, groups))}
        className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg px-4 py-2 transition-colors"
        title="One row per email address — the mail-out list, with shared addresses combined"
      >
        ⬇ Recipient list (CSV)
      </button>
    </div>
  );

  if (!isConfigured()) {
    return (
      <div className="p-6 max-w-2xl">
        <h1 className="font-display text-2xl text-brand-blue mb-2">Email schedules</h1>
        <div className="mb-4">{exportButtons}</div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
          <p className="font-semibold mb-1">Google sign-in isn't set up yet.</p>
          <p>
            Create an OAuth client ID for the club's Google Workspace and put it in{" "}
            <code className="bg-amber-100 px-1 rounded">REACT_APP_GOOGLE_CLIENT_ID</code>, then
            rebuild. The steps are in the comment at the top of{" "}
            <code className="bg-amber-100 px-1 rounded">src/utils/gmail.js</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div>
          <h1 className="font-display text-2xl text-brand-blue">Email schedules</h1>
          <p className="text-sm text-gray-500">
            One email per address — a parent registered for two juniors gets a single message.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {account ? (
            <>
              <span className="text-xs text-gray-500">
                Sending as <span className="font-medium text-gray-700">{account}</span>
              </span>
              <button
                onClick={handleDisconnect}
                disabled={sending}
                className="text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 transition-colors disabled:opacity-50"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-brand-blue hover:bg-blue-900 text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors disabled:opacity-60"
            >
              {connecting ? "Connecting…" : "Connect Gmail"}
            </button>
          )}
        </div>
      </div>

      {authError && (
        <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {authError}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose + recipients */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Term
              </label>
              <input
                value={term}
                onChange={e => setTerm(e.target.value)}
                placeholder="e.g. Fall 2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="text-xs text-gray-400 mt-1">
                Replaces <code>{"{term}"}</code> in the subject and intro.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Subject
              </label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <RichField
              label="Intro"
              value={intro}
              onChange={setIntro}
              rows={3}
              id="email-intro"
            />
            <RichField
              label="Sign-off"
              value={signoff}
              onChange={setSignoff}
              rows={4}
              id="email-signoff"
            />
            <p className="text-xs text-gray-400">
              Line breaks are kept. To link text, write{" "}
              <code className="bg-gray-100 px-1 rounded">[club rules](https://…)</code> — or paste a
              bare web address or email address and it becomes a link on its own.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeGroupmates}
                onChange={e => setIncludeGroupmates(e.target.checked)}
                className="rounded border-gray-300"
              />
              Include the names of others in each group
            </label>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-700">
                <span className="font-semibold">{selected.size}</span> of {recipients.length} addresses selected
                {sentCount > 0 && (
                  <span className="text-gray-400"> · {sentCount} already sent this term</span>
                )}
              </div>
              <div className="flex gap-2 text-xs">
                <button
                  onClick={() => setSelected(new Set(recipients.filter(r => r.sessionCount > 0).map(r => r.email)))}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Select all with sessions
                </button>
                <button onClick={() => setSelected(new Set())} className="text-gray-400 hover:text-gray-700">
                  Clear
                </button>
                {sentCount > 0 && (
                  <button
                    onClick={() =>
                      setSelected(new Set(
                        recipients
                          .filter(r => r.sessionCount > 0 && statusFor(r.email) !== "sent")
                          .map(r => r.email)
                      ))
                    }
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Only not-yet-sent
                  </button>
                )}
              </div>
            </div>

            {(withoutSessions > 0 || unreachable > 0) && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
                {withoutSessions > 0 && (
                  <div>{withoutSessions} address{withoutSessions === 1 ? "" : "es"} have no scheduled session — left unselected.</div>
                )}
                {unreachable > 0 && (
                  <div>{unreachable} player{unreachable === 1 ? "" : "s"} on the roster have no email address and no household to reach them through — link them in Households to include them.</div>
                )}
              </div>
            )}

            <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
              {recipients.map(r => {
                const status = statusFor(r.email);
                return (
                  <div
                    key={r.email}
                    className={`flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer ${
                      previewEmail === r.email ? "bg-blue-50/60" : ""
                    }`}
                    onClick={() => setPreviewEmail(r.email)}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(r.email)}
                      onChange={() => toggle(r.email)}
                      onClick={e => e.stopPropagation()}
                      className="mt-1 rounded border-gray-300"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-800 truncate">{r.email}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {r.people.map(p => p.name).join(", ")} ·{" "}
                        {r.sessionCount === 0
                          ? <span className="text-amber-600">no sessions</span>
                          : `${r.sessionCount} session${r.sessionCount === 1 ? "" : "s"}`}
                      </div>
                      {status === "failed" && termLog[r.email]?.error && (
                        <div className="text-xs text-red-600 mt-0.5">{termLog[r.email].error}</div>
                      )}
                    </div>
                    {status === "sent" && (
                      <span className="text-[11px] font-semibold text-green-700 bg-green-100 rounded px-1.5 py-0.5">Sent</span>
                    )}
                    {status === "failed" && (
                      <span className="text-[11px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5">Failed</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Preview + send */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Preview</p>
              {preview ? (
                <>
                  <p className="text-sm text-gray-800 mt-1 truncate">To: {preview.to}</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{preview.subject}</p>
                </>
              ) : (
                <p className="text-sm text-gray-500 mt-1">No recipients with an email address.</p>
              )}
            </div>
            {preview && (
              <iframe
                title="Email preview"
                srcDoc={preview.html}
                sandbox=""
                className="w-full h-[460px] bg-gray-50"
              />
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Check before sending
              </p>
              {exportButtons}
            </div>
            <div className="border-t border-gray-100 pt-3" />
            {!account && (
              <p className="text-sm text-gray-500">Connect Gmail to send a test or the batch.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleTestSend}
                disabled={!account || sending || !previewRecipient}
                className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
              >
                Send test to myself
              </button>
              <button
                onClick={() => setConfirming(true)}
                disabled={!ready}
                className="bg-brand-blue hover:bg-blue-900 text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
              >
                Send to {toSend.length} recipient{toSend.length === 1 ? "" : "s"}
              </button>
              {failed.length > 0 && !sending && (
                <button
                  onClick={() => runSend(failed)}
                  disabled={!account}
                  className="border border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
                >
                  Retry {failed.length} failed
                </button>
              )}
            </div>
            {term.trim() === "" && (
              <p className="text-xs text-amber-700">Enter a term name before sending.</p>
            )}
            {progress && (
              <div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-blue transition-all"
                    style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Sending {progress.done} of {progress.total} — keep this tab open.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirming(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-display text-xl text-brand-blue mb-2">Send {toSend.length} emails?</h2>
            <p className="text-sm text-gray-600 mb-1">
              They'll go out from <span className="font-medium">{account}</span> and can't be recalled.
            </p>
            {toSend.some(r => statusFor(r.email) === "sent") && (
              <p className="text-sm text-amber-700 mb-1">
                {toSend.filter(r => statusFor(r.email) === "sent").length} of these were already emailed this term and will get a second copy.
              </p>
            )}
            <p className="text-xs text-gray-400 mb-4">
              Takes about {Math.ceil((toSend.length * (SEND_DELAY_MS + 400)) / 1000 / 60)} minute(s). Leave this tab open.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="text-sm text-gray-500 hover:text-gray-800 px-4 py-2">
                Cancel
              </button>
              <button
                onClick={() => runSend(toSend)}
                className="bg-brand-blue hover:bg-blue-900 text-white text-sm font-semibold rounded-lg px-4 py-2"
              >
                Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
